import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EyeIcon, EyeOffIcon } from '@untitledui-icons/react/line'
import { AuthLayout } from '@/components/auth'
import { Button } from '@/components/base/buttons/button'
import { useAuth } from '@/hooks/api'
import { loginSchema, type LoginFormValues } from '@/schemas/auth'
import { getErrorMessage } from '@/lib/api'

export function LoginPage() {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const { login, isLoggingIn, loginError } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = (data: LoginFormValues) => {
    login(data)
  }

  return (
    <AuthLayout
      title={t('app.auth.welcome_back')}
      subtitle={t('app.auth.sign_in_subtitle')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email field */}
        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.email')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('app.auth.enter_your_email')}
            className={`
              w-full rounded-lg border px-3.5 py-2.5 text-sm
              placeholder:text-gray-400
              focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
              disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
              dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
              ${
                errors.email
                  ? 'border-error-500 focus:ring-error-500'
                  : 'border-gray-300 dark:border-gray-600'
              }
            `}
            disabled={isLoggingIn}
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-1.5 text-sm text-error-500">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password field */}
        <div>
          <label
            htmlFor="password"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.password')}
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('app.auth.enter_your_password')}
              className={`
                w-full rounded-lg border px-3.5 py-2.5 pr-10 text-sm
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
                disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
                dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
                ${
                  errors.password
                    ? 'border-error-500 focus:ring-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }
              `}
              disabled={isLoggingIn}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-sm text-error-500">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me & Forgot password */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('app.auth.remember_me')}
            </span>
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            {t('app.auth.forgot_password')}
          </Link>
        </div>

        {/* Error message */}
        {loginError && (
          <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
            {getErrorMessage(loginError)}
          </div>
        )}

        {/* Submit button */}
        <Button
          type="submit"
          size="lg"
          color="primary"
          isDisabled={isLoggingIn}
          isLoading={isLoggingIn}
          showTextWhileLoading
          className="w-full"
        >
          {isLoggingIn ? t('app.auth.signing_in') : t('app.auth.sign_in')}
        </Button>

        <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          {t('app.auth.terms_agreement')}
          <a
            href="https://bonnie.ai/terms-of-service/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            {t('app.auth.terms_of_service')}
          </a>
          {t('app.auth.and')}
          <a
            href="https://bonnie.ai/privacy-statement/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            {t('app.auth.privacy_statement')}
          </a>
          .
        </p>
      </form>
    </AuthLayout>
  )
}

export default LoginPage
